// Sales Pipeline — Tabbed list view.
//
// One row = one `<li>` container. The top bar renders identically
// whether the row is collapsed or expanded (per Aug 2 spec — the two
// email actions must not move). Below the top bar, the expanded
// state reveals a 4-column grid: Summary | Move to Stage | Plan +
// Convert stack | Notes, with Follow-up Status spanning columns
// 2 + 3 beneath.
//
// The whole selected row + expanded content sits inside one
// container so the tinted background, rounded corners and thicker
// keyline wrap it as a single unit.

import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Flame, MapPin, MessageSquare, FileText, ChevronDown, ChevronUp,
  Mail, MailX, Send, Target, Award,
  Link2, CheckCircle2, Calendar, Phone, Pencil,
  User as UserIcon, Clock, Save, X as XIcon,
  ArrowDownCircle, ArrowRightLeft, Copy, Globe, Users, ExternalLink,
  Check, Loader2,
} from "lucide-react";
import { AdminNotesEditor, InterestedChecklist, TemperaturePicker } from "@/pages/ContactsPage";
import CorrespondenceModal from "@/components/pipeline/CorrespondenceModal";
import ContactTerritoryMapInline from "@/components/contacts/ContactTerritoryMapInline";
import MojoJourney from "@/components/mojo-journey/MojoJourney";
import { formatSubmittedAt } from "@/components/pipeline/confidentialityFormat";
import { buildDiscoveryPortalEmail, openDiscoveryPortalEmail } from "@/utils/discoveryPortalEmail.mjs";

const TABS = [
  { key: "qualified",     label: "Interested",     dot: "bg-emerald-500", bg: "bg-emerald-600", fg: "text-white" },
  { key: "new",           label: "New",            dot: "bg-stone-400",   bg: "bg-stone-900",   fg: "text-white" },
  { key: "contacted",     label: "Contacted",      dot: "bg-blue-400",    bg: "bg-blue-600",    fg: "text-white" },
  { key: "follow_up_due", label: "Follow-up Due",  dot: "bg-amber-500",   bg: "bg-amber-500",   fg: "text-stone-950" },
];

// Six stages. ``activeCls`` renders the selected pill as a solid
// coloured block matching the tab bar so admins instantly see which
// stage the contact sits in.
const STAGE = {
  new:           { label: "New",            cls: "bg-white text-stone-700 border-stone-300", activeCls: "bg-stone-900 text-white border-stone-900" },
  contacted:     { label: "Contacted",      cls: "bg-white text-stone-700 border-stone-300", activeCls: "bg-blue-600 text-white border-blue-600" },
  qualified:     { label: "Interested",     cls: "bg-white text-stone-700 border-stone-300", activeCls: "bg-emerald-600 text-white border-emerald-600" },
  dormant:       { label: "Dormant",        cls: "bg-white text-stone-700 border-stone-300", activeCls: "bg-orange-500 text-white border-orange-500" },
  follow_up_due: { label: "Follow-up Due",  cls: "bg-white text-stone-700 border-stone-300", activeCls: "bg-amber-500 text-stone-950 border-amber-500" },
  lost:          { label: "Lost",           cls: "bg-white text-stone-700 border-stone-300", activeCls: "bg-red-600 text-white border-red-600" },
};

// (STAGE_TOPBAR removed — mockup shows no stage pill in the top bar;
// the active tab implies the stage.)

// Three-tier heat scale — cold=blue, warm=purple, hot=orange (matches
// the flame swatches). ``card`` is the tint that wraps the entire
// selected row + expanded content as one unit.
// Row-tint palette keyed on the manual pipeline temperature (Hot / Keen /
// Lukewarm). Mirrors the flame colours used on the kanban card so the
// two views feel like the same data. `row` is the soft tint applied to
// each collapsed row when a temperature is set; `card` wraps the whole
// expanded card with a thicker tinted border.
const HEAT = {
  hot:      { label: "Hot",      row: "bg-orange-50", card: "border-orange-300" },
  keen:     { label: "Keen",     row: "bg-purple-50", card: "border-purple-300" },
  lukewarm: { label: "Lukewarm", row: "bg-blue-50",   card: "border-blue-300"   },
};

function daysSinceISO(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Compact date + time stamp for the row summary — "12/08/2026 14:32"
// (UK locale, 24-hour). Returns null when there's no usable timestamp
// so the JSX can gracefully hide the block instead of showing "—".
function formatDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return { date, time };
}

function initialsFor(contact) {
  const parts = [contact.first_name, contact.last_name].filter(Boolean);
  if (parts.length === 0) return (contact.email || "??")[0].toUpperCase() + "?";
  return parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase();
}

function displayNameFor(contact) {
  return [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "(no name)";
}

// ---------------------------------------------------------------------
// Top-level tabs view
// ---------------------------------------------------------------------
export default function SalesPipelineTabsView({
  contacts,
  tempMap,
  onOpenContact,
  onReplyContact,
  onContactUpdated,
  onOpenPostcodeMap,
  onStageChange,
  onDemote,
  onConvert,
  onLinkExisting,
  onMarkFollowUpSent,
  onChangeSource,
  onTemperatureChange,
  // Multi-select support — driven from ContactsPage so the bulk
  // action bar (with Bulk Reply / Move / Delete) works identically
  // whether the admin is in the LIST, PIPELINE, or TABS view. Both
  // props are optional so any legacy consumer that renders this
  // view without selection still works. Feb 2026 Sam brief.
  selectedIds,
  onToggleSelect,
  // Deep-linking: when the Dashboard Journey Overview links to a
  // specific contact, ContactsPage passes the id here so we can auto-
  // switch to the correct pipeline tab, expand that row and scroll it
  // into view. `onInitialExpandedConsumed` is called once we've acted
  // on the id so ContactsPage can null it out (prevents the row
  // snapping back if the admin later collapses it manually).
  initialExpandedId,
  onInitialExpandedConsumed,
}) {
  const selectionSet = selectedIds instanceof Set ? selectedIds : new Set();
  const selectable = typeof onToggleSelect === "function";
  void tempMap;
  const [activeTab, setActiveTab] = useState("qualified");
  const [userPickedTab, setUserPickedTab] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [correspondenceContact, setCorrespondenceContact] = useState(null);

  // Honour `initialExpandedId` from the Dashboard deep-link exactly
  // once the target contact has arrived in `contacts`. We flip
  // userPickedTab=true so the auto-first-non-empty-tab effect above
  // doesn't fight us, then scroll the row into view after paint.
  React.useEffect(() => {
    if (!initialExpandedId) return;
    const target = contacts.find((c) => c.id === initialExpandedId && c.in_pipeline);
    // Contacts arrive asynchronously (and may be paged) — keep the
    // pending id alive until we actually find the row. `contacts.length`
    // in the dep array re-runs us on each fresh batch.
    if (!target) return;
    const stage = target.pipeline_status || "new";
    if (["new", "contacted", "follow_up_due", "qualified"].includes(stage)) {
      setActiveTab(stage);
      setUserPickedTab(true);
    }
    setExpandedId(initialExpandedId);
    // Give the DOM a beat to render the expanded body before scrolling.
    const t = window.setTimeout(() => {
      const el = document.querySelector(
        `[data-testid="pipeline-row-${initialExpandedId}"]`
      );
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 120);
    onInitialExpandedConsumed?.();
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialExpandedId, contacts.length]);

  const buckets = React.useMemo(() => {
    const out = { new: [], contacted: [], follow_up_due: [], qualified: [] };
    for (const c of contacts) {
      if (!c.in_pipeline) continue;
      const s = c.pipeline_status || "new";
      if (out[s]) out[s].push(c);
    }
    return out;
  }, [contacts]);

  // Auto-pick the first non-empty tab on load so admins land on a
  // populated list instead of the empty "New" bucket. Stops as soon as
  // the user manually clicks a tab (userPickedTab flag) so this never
  // overrides an explicit choice.
  React.useEffect(() => {
    if (userPickedTab) return;
    if ((buckets[activeTab] || []).length > 0) return;
    const firstWithData = TABS.find((t) => (buckets[t.key] || []).length > 0);
    if (firstWithData && firstWithData.key !== activeTab) {
      setActiveTab(firstWithData.key);
    }
  }, [buckets, activeTab, userPickedTab]);

  const activeRows = buckets[activeTab] || [];

  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="sales-pipeline-tabs">
      <div className="flex items-center justify-between gap-2 border-b border-stone-200 bg-white">
        <div role="tablist" className="flex gap-1.5 p-1.5 flex-1">
          {TABS.map((t) => {
            const isActive = t.key === activeTab;
            const count = (buckets[t.key] || []).length;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => { setActiveTab(t.key); setUserPickedTab(true); setExpandedId(null); }}
                data-testid={`pipeline-tab-${t.key}`}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] transition-colors rounded-lg ${
                  isActive
                    ? `${t.bg} ${t.fg}`
                    : "bg-[#F2F2F0] text-stone-500 hover:text-stone-800 hover:bg-stone-100"
                }`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${isActive ? "bg-white/70" : t.dot}`} />
                <span>{t.label}</span>
                <span
                  className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded-full ${
                    isActive ? "bg-black/20 text-white" : "bg-stone-200 text-stone-700"
                  } ${isActive && t.fg.includes("stone-950") ? "!bg-stone-900/15 !text-stone-950" : ""}`}
                  data-testid={`pipeline-tab-count-${t.key}`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <SalesPackBackfillButton />
      </div>

      {activeRows.length === 0 ? (
        <div className="py-16 text-center text-sm text-stone-500" data-testid={`pipeline-tab-empty-${activeTab}`}>
          No contacts in this stage right now.
        </div>
      ) : (
        <ul className="p-1.5 space-y-1.5" data-testid="pipeline-rows">
          {activeRows.map((c) => (
            <PipelineRow
              key={c.id}
              contact={c}
              isExpanded={expandedId === c.id}
              // When any row is expanded, dim every OTHER row so the
              // eye stays on the focused entry. The expanded row keeps
              // full opacity + gets the tint/keyline treatment; the
              // rest fade to ~35% until the user collapses.
              isDimmed={expandedId !== null && expandedId !== c.id}
              onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
              onOpenContact={onOpenContact}
              onReplyContact={onReplyContact}
              onContactUpdated={onContactUpdated}
              onOpenPostcodeMap={onOpenPostcodeMap}
              onStageChange={onStageChange}
              onDemote={onDemote}
              onConvert={onConvert}
              onLinkExisting={onLinkExisting}
              onMarkFollowUpSent={onMarkFollowUpSent}
              onChangeSource={onChangeSource}
              onTemperatureChange={onTemperatureChange}
              onViewCorrespondence={() => setCorrespondenceContact(c)}
              selectable={selectable}
              isSelected={selectionSet.has(c.id)}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </ul>
      )}

      {correspondenceContact && (
        <CorrespondenceModal
          open={true}
          contact={correspondenceContact}
          onClose={() => setCorrespondenceContact(null)}
          onOpenPostcodeMap={onOpenPostcodeMap}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// A single row — unified container. Top bar renders in BOTH collapsed
// and expanded states with the same slot for the two email actions.
// Expanded content sits directly below the top bar inside the same
// tinted outer container so the keyline wraps them as one unit.
function PipelineRow({
  contact, isExpanded, isDimmed, onToggle,
  onOpenContact, onReplyContact, onContactUpdated, onOpenPostcodeMap,
  onStageChange, onDemote, onConvert, onLinkExisting, onMarkFollowUpSent,
  onChangeSource, onTemperatureChange,
  onViewCorrespondence,
  // Multi-select support — optional so any legacy caller that
  // doesn't pass these still gets a checkbox-less row. When
  // ``selectable`` is true, a small square-icon checkbox renders on
  // the left of the row (before the header). ``onToggleSelect`` is
  // invoked with the contact id + shift-key state so the standard
  // bulk-select flow works.
  selectable = false,
  isSelected = false,
  onToggleSelect = null,
}) {
  const c = contact;
  const heat = HEAT[c.temperature] || null;
  const emailed = (c.email_sends_count || 0) > 0;
  const daysInStage = daysSinceISO(c.pipeline_status_updated_at || c.updated_at || c.date_added);

  const rowTint = heat ? heat.row : "bg-white";
  const borderTint = heat ? heat.card : "border-stone-200";

  return (
    <li
      data-testid={`pipeline-row-${c.id}`}
      data-dimmed={isDimmed ? "true" : undefined}
      className={`${rowTint} border ${borderTint} rounded-xl overflow-hidden transition-opacity duration-200 ${
        isDimmed ? "opacity-35 hover:opacity-70" : "opacity-100"
      }`}
    >
      {/* Single outer container. When expanded we thicken the keyline
          so the whole unit reads as one, but the fill stays the same
          tinted colour as the collapsed row so the heat is obvious. */}
      <div
        className={
          isExpanded
            ? `m-3 border-2 ${borderTint} rounded-2xl overflow-hidden shadow-sm bg-[#F2F2F0]`
            : "border-2 border-transparent rounded-xl"
        }
        data-testid={isExpanded ? `pipeline-row-expanded-${c.id}` : undefined}
      >
        <TopBar
          contact={c}
          emailed={emailed}
          daysInStage={daysInStage}
          isExpanded={isExpanded}
          onToggle={onToggle}
          onOpenPostcodeMap={onOpenPostcodeMap}
          onReplyContact={onReplyContact}
          onViewCorrespondence={() => onViewCorrespondence?.(c)}
          onTemperatureChange={onTemperatureChange}
          onContactUpdated={onContactUpdated}
          selectable={selectable}
          isSelected={isSelected}
          onToggleSelect={onToggleSelect}
        />

        {isExpanded && (
          <ExpandedBody
            contact={c}
            daysInStage={daysInStage}
            onOpenPostcodeMap={onOpenPostcodeMap}
            onContactUpdated={onContactUpdated}
            onOpenContact={onOpenContact}
            onStageChange={onStageChange}
            onDemote={onDemote}
            onConvert={onConvert}
            onLinkExisting={onLinkExisting}
            onMarkFollowUpSent={onMarkFollowUpSent}
            onChangeSource={onChangeSource}
          />
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------
// Top bar — identical layout in both collapsed and expanded states.
// Left cluster: avatar + name + days-in-stage + email.
// Right cluster: Reply-with-Template + View-Correspondence buttons,
// postcode + MAP, heat, emailed chip, chevron.
// ---------------------------------------------------------------------
// QUICK NOTE — single-line free-text visual reference. Persists via
// PATCH /contacts/{id}/details (whitelisted field on the backend).
// Whatever the admin types here is ALSO appended, timestamped, to the
// contact's running ``admin_notes`` field so the row-level scratchpad
// never overwrites earlier jots — the expanded NOTES panel keeps the
// full history. Uncontrolled input so it never re-renders while
// typing; saves on blur or Enter. Escape reverts. A live ``title``
// tooltip mirrors the current value so the full note stays readable
// even when the column is narrow — no info gets truncated behind the
// visible input width across Chrome, Safari and Firefox.
function QuickNoteInput({ contact, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [tooltip, setTooltip] = useState(contact.quick_note || "");
  const ref = React.useRef(null);
  const initial = contact.quick_note || "";

  React.useEffect(() => {
    // Sync when the underlying contact swaps (parent re-fetches, etc.)
    if (ref.current && ref.current.value !== (contact.quick_note || "")) {
      ref.current.value = contact.quick_note || "";
    }
    setTooltip(contact.quick_note || "");
  }, [contact.id, contact.quick_note]);

  const commit = async () => {
    const next = (ref.current?.value || "").trim();
    if (next === (contact.quick_note || "")) return;
    setError(false);
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/contacts/${contact.id}/details`,
        { quick_note: next || null },
      );
      // Reflect the saved value back onto the contact object so the
      // useEffect above doesn't overwrite it on the next render.
      contact.quick_note = next || null;
      // Propagate every field the backend echoed — includes the newly
      // appended ``admin_notes`` and ``admin_notes_updated_at`` so the
      // expanded NOTES panel picks up the timestamped entry without a
      // page reload.
      if (data && onSaved) {
        const patch = {};
        if ("quick_note" in data) patch.quick_note = data.quick_note;
        if ("admin_notes" in data) patch.admin_notes = data.admin_notes;
        if ("admin_notes_updated_at" in data) patch.admin_notes_updated_at = data.admin_notes_updated_at;
        if (Object.keys(patch).length) onSaved(contact.id, patch);
      }
    } catch (e) {
      setError(true);
      // Revert visible input so the admin can retry
      if (ref.current) ref.current.value = contact.quick_note || "";
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      ref={ref}
      type="text"
      defaultValue={initial}
      placeholder="Quick note…"
      onBlur={commit}
      onInput={(e) => setTooltip(e.currentTarget.value || "")}
      onKeyDown={(e) => {
        // Stop keydown bubbling so Space/Enter/Arrow keys stay inside
        // the input instead of triggering the row's toggle button or
        // any parent handlers (Space was collapsing/expanding the row).
        e.stopPropagation();
        if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
        if (e.key === "Escape") {
          if (ref.current) ref.current.value = contact.quick_note || "";
          setTooltip(contact.quick_note || "");
          e.currentTarget.blur();
        }
      }}
      onKeyUp={(e) => e.stopPropagation()}
      onKeyPress={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      disabled={saving}
      title={error ? "Save failed — try again" : (tooltip || "Free-text visual reference")}
      className={`w-full text-xs bg-white/70 border rounded-md px-2 py-1 placeholder:text-stone-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-stone-900 transition-colors ${
        error ? "border-red-300" : "border-stone-200 hover:border-stone-300"
      }`}
      data-testid={`pipeline-row-quicknote-${contact.id}`}
    />
  );
}

function TopBar({
  contact, emailed, daysInStage,
  isExpanded, onToggle, onOpenPostcodeMap, onReplyContact, onViewCorrespondence,
  onTemperatureChange, onContactUpdated,
  selectable, isSelected, onToggleSelect,
}) {
  const c = contact;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/40 transition-colors ${
        isExpanded ? "border-b border-stone-200/70" : ""
      }`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle?.(); } }}
      data-testid={`pipeline-row-toggle-${c.id}`}
    >
      {/* Multi-select checkbox — leftmost when selection is enabled.
          Click bubbling stopped so ticking a box doesn't also toggle
          the row expansion. Sam's Feb 2026 Bulk Reply brief. */}
      {selectable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.(c.id, e);
          }}
          data-testid={`pipeline-row-select-${c.id}`}
          aria-pressed={isSelected}
          aria-label={isSelected ? "Deselect contact" : "Select contact"}
          className={`shrink-0 w-5 h-5 flex items-center justify-center rounded border transition-colors ${
            isSelected
              ? "bg-stone-950 border-stone-950 text-[#dddd16]"
              : "bg-white border-stone-300 hover:border-stone-500"
          }`}
        >
          {isSelected ? (
            <Check className="w-3 h-3" strokeWidth={3} />
          ) : (
            <span className="w-2.5 h-2.5" />
          )}
        </button>
      )}
      {/* Avatar (only in expanded state — collapsed rows stay
          light to keep the list scannable). */}
      {isExpanded && (
        <div className="w-10 h-10 rounded-full bg-white/80 text-stone-800 font-bold text-sm flex items-center justify-center border border-white shrink-0">
          {initialsFor(c)}
        </div>
      )}

      {/* Left cluster — name + email. Fixed width so the middle
          quick-note area always starts at the same column. */}
      <div className="w-[240px] shrink-0 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`truncate font-bold text-stone-950 ${isExpanded ? "text-base" : "text-sm"}`}>
            {displayNameFor(c)}
          </span>
          {typeof daysInStage === "number" && (
            <span className="text-[11px] text-stone-500 shrink-0">
              {daysInStage} in stage
            </span>
          )}
        </div>
        <div className="text-[11px] text-stone-600 truncate mt-0.5">
          {c.email || <span className="italic text-stone-400">no email</span>}
        </div>
      </div>

      {/* Date & time the contact was captured — sits between the
          name/email block and the Quick Note input so admins can
          see at a glance how fresh each lead is. Fixed 108px so the
          Quick Note lozenge always starts at the same column across
          every row. Sam's Feb 2026 brief. Hidden gracefully when
          the contact has no usable timestamp. */}
      {(() => {
        const dt = formatDateTime(c.date || c.date_added || c.created_at);
        return (
          <div
            className="w-[108px] shrink-0 text-[11px] leading-tight text-stone-600 tabular-nums"
            data-testid={`pipeline-row-datetime-${c.id}`}
          >
            {dt ? (
              <>
                <div className="font-medium text-stone-800">{dt.date}</div>
                <div className="text-stone-500">{dt.time}</div>
              </>
            ) : (
              <span className="italic text-stone-400">—</span>
            )}
          </div>
        );
      })()}

      {/* Middle cluster — free-text quick note. Single line, saved
          on blur. Purely a visual reference for the admin; not
          linked to templates, pipeline logic, or exports. */}
      <div
        className="flex-1 min-w-0 px-2"
        onClick={(e) => e.stopPropagation()}
      >
        <QuickNoteInput contact={c} onSaved={onContactUpdated} />
      </div>

      {/* Right cluster — persistent email actions + info chips.
          Each item lives in a fixed-width slot so columns line up
          neatly across every row. Stop-propagation so clicks don't
          collapse the row. */}
      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="hidden md:flex w-[172px] justify-start">
          <button
            type="button"
            onClick={() => onReplyContact?.(c, { mode: "template" })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#c9c914] rounded-full whitespace-nowrap"
            data-testid={`pipeline-row-template-reply-${c.id}`}
          >
            <Send className="w-3 h-3" /> Reply with Template
          </button>
        </div>

        <div className="hidden md:flex w-[180px] justify-start">
          <button
            type="button"
            onClick={onViewCorrespondence}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-white border border-stone-300 text-stone-800 hover:bg-stone-50 rounded-full whitespace-nowrap"
            data-testid={`pipeline-row-view-correspondence-${c.id}`}
          >
            <FileText className="w-3 h-3" /> View Correspondence
          </button>
        </div>

        <div className="w-[70px] text-right">
          {c.postcode ? (
            <span className="text-xs text-stone-800 tabular-nums">{c.postcode}</span>
          ) : (
            <span className="text-[11px] text-stone-400 italic">no postcode</span>
          )}
        </div>

        <div className="w-[62px] flex justify-start">
          {c.postcode ? (
            <button
              type="button"
              onClick={() => onOpenPostcodeMap?.(c)}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-stone-300 rounded hover:bg-stone-100 text-stone-700"
              data-testid={`pipeline-row-postcode-map-btn-${c.id}`}
              title={`See ${c.postcode} on the UK territory atlas`}
            >
              <MapPin className="w-2.5 h-2.5" /> Map
            </button>
          ) : (
            <span className="text-[10px] text-stone-300">—</span>
          )}
        </div>

        <span
          className="inline-flex items-center justify-center w-[82px]"
          data-testid={`pipeline-row-temp-${c.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <TemperaturePicker
            value={c.temperature || null}
            onChange={(next) => onTemperatureChange?.(c.id, next)}
            size="sm"
            testidPrefix={`pipeline-row-temp-${c.id}`}
          />
        </span>

        <div className="w-[112px] flex justify-start">
          <span
            className={`inline-flex items-center justify-center gap-1 w-full text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
              emailed
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-stone-100 text-stone-500 border-stone-200"
            }`}
            data-testid={`pipeline-row-emailed-${c.id}`}
          >
            {emailed ? <Mail className="w-3 h-3" /> : <MailX className="w-3 h-3" />}
            {emailed ? "Emailed" : "Not emailed"}
          </span>
        </div>

        <button
          type="button"
          onClick={onToggle}
          className="text-stone-500 hover:text-stone-800 shrink-0"
          data-testid={`pipeline-row-chevron-${c.id}`}
        >
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Expanded body — 2026-08-17 redesign.
//
// The customer's geographical position vs current franchisees is one
// of the first things HQ needs to see when reviewing a new enquiry,
// so the layout is now:
//
//   Row 1  ─  [ Summary ] [ Location & Territory Map (dominant) ]
//                                     +  compact right rail:
//                                        [ Plan Territory ]  [ Checklist ]
//                                        [ Convert ] [ Change Type ] [ Follow-up ]
//   Row 2  ─  [ Discovery Portal (50%) ] [ Notes & related (50%, 2-col) ]
//   Row 3  ─  [ Move to Stage — full width ]
//
// Responsive: at <lg the whole thing stacks single-column so the map
// still sits second (right after the summary), preserving the "see
// their location first" priority the customer wanted.
//
// The map component is `ContactTerritoryMapInline` which reuses the
// same atlas + geocoder endpoints as the (still-existing, drawer-only)
// ContactPostcodeMapModal — no duplicate map data or divergent styling.
function ExpandedBody({
  contact, daysInStage, onContactUpdated, onOpenContact,
  onStageChange, onDemote, onConvert, onLinkExisting, onMarkFollowUpSent,
  onChangeSource,
}) {
  const c = contact;
  return (
    <div className="p-4 flex flex-col gap-3" data-testid={`pipeline-row-body-${c.id}`}>
      {/* Mojo Journey tracker (admin-only visual progress). Sits at the
          top so Sandra sees the current Interested progress before any
          detail panels. Derived steps sync from the underlying data;
          the FLEX-information step is HQ-managed manual. Clicking a
          step cycles Done ↔ N/A ↔ To do. */}
      <MojoJourney journey="interested" entityId={c.id} />

      {/* ── Row 1 ── Summary | Map (dominant, fills panel) | Right rail stack */}
      <div className="grid gap-3 grid-cols-1 lg:grid-cols-12 lg:auto-rows-fr">
        {/* Summary — narrow left column */}
        <div className="lg:col-span-2 min-w-0">
          <SummaryPanel
            contact={c}
            daysInStage={daysInStage}
            onSaved={(patch) => onContactUpdated?.(c.id, patch)}
          />
        </div>

        {/* Location & Territory map — the dominant panel. The map itself
            uses fill mode inside PanelShell body so it grows to match
            the neighbouring right-rail column height, eliminating the
            large blank area under the map. */}
        <div className="lg:col-span-5 min-w-0 flex flex-col">
          <PanelShell
            icon={MapPin}
            title="Location & Territory Map"
            testId={`pipeline-panel-map-${c.id}`}
            bodyClass="!p-0"
          >
            <ContactTerritoryMapInline contact={c} fill />
          </PanelShell>
        </div>

        {/* Right rail — vertical stack per the 2026-08-17 spec:
              1. Checklist (top, spans full rail width, landscape-orientated)
              2. Plan Their Territory (shallow single-line panel)
              3. Convert | Change Type | Follow-up Status (3-col row) */}
        <div className="lg:col-span-5 min-w-0 flex flex-col gap-3">
          <div className="min-w-0"><InterestedChecklist contact={c} onChanged={(patch) => onContactUpdated?.(c.id, patch)} /></div>
          <div className="min-w-0"><TerritoryPanel contact={c} compact /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
            <div className="min-w-0"><ConvertPanel contact={c} onConvert={onConvert} onLinkExisting={onLinkExisting} /></div>
            <div className="min-w-0"><ChangeTypePanel contact={c} onChangeSource={onChangeSource} /></div>
            <div className="min-w-0"><FollowUpPanel contact={c} onMarkFollowUpSent={onMarkFollowUpSent} /></div>
          </div>
        </div>
      </div>

      {/* ── Row 2 ── Discovery Portal (50%) | Notes & related (50%, 2-col) */}
      <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
        <div className="min-w-0">
          <DiscoveryPortalPanel contact={c} />
        </div>
        <div className="min-w-0">
          <NotesAndRelatedPanel contact={c} onContactUpdated={onContactUpdated} />
        </div>
      </div>

      {/* ── Row 3 ── Move to Stage (full width) */}
      <div className="min-w-0">
        <StagePanel
          contact={c}
          onStageChange={onStageChange}
          onDemote={onDemote}
          stretch
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// (Historical ChecklistPanel removed — the tab view now renders the
// shared InterestedChecklist widget directly so the styling matches
// the Kanban modal exactly.)

// ---------------------------------------------------------------------
// Panel shell — a single card style used by every expanded panel so
// the layout reads as one composed unit rather than a collection of
// unrelated boxes.
// Per the Feb-2026 visual guide, every mini-panel in the expanded row
// wears a thicker blue keyline + soft blue tint so the four columns are
// visually parseable at a glance. Semantic tones (tinted/amber/emerald)
// still exist for backward-compat but now all map to the same blue
// treatment — the semantic meaning is carried by inner buttons/labels,
// not the panel chrome itself.
function PanelShell({ icon: Icon, title, action, children, testId, tone = "default", bodyClass = "" }) {
  void tone;
  const shellCls = "border-2";
  return (
    <section
      className={`h-full w-full rounded-xl flex flex-col overflow-hidden ${shellCls}`}
      style={{ backgroundColor: "#fafbe9", borderColor: "#dddd16" }}
      data-testid={testId}
    >
      <header
        className="flex items-center gap-2 px-3 py-2 border-b"
        style={{ borderColor: "#e6e37f" }}
      >
        {Icon && <Icon className="w-3.5 h-3.5 text-stone-500" />}
        <h4 className="text-[11px] uppercase tracking-[0.16em] font-bold text-stone-700">{title}</h4>
        {action && <div className="ml-auto">{action}</div>}
      </header>
      <div className={`p-3 text-xs text-stone-800 flex-1 min-h-0 ${bodyClass}`}>
        {children}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------
// SUMMARY — matches the reference layout: email, phone, town, postcode
// with MAP button, then date + age at the BOTTOM, with breathing room
// in between. Edit lives top-right of the panel header.
function SummaryPanel({ contact, daysInStage, onSaved }) {
  const c = contact;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    first_name: c.first_name || "",
    last_name: c.last_name || "",
    email: c.email || "",
    telephone: c.telephone || c.phone || "",
    address_line_1: c.address_line_1 || "",
    address_line_2: c.address_line_2 || "",
    city: c.city || "",
    postcode: c.postcode || "",
    county: c.county || "",
    country: c.country || "",
  });

  async function save() {
    setSaving(true); setErr("");
    try {
      const diff = {};
      for (const k of Object.keys(form)) {
        const prev = c[k] || (k === "telephone" ? c.phone || "" : "");
        if (form[k] !== prev) diff[k] = form[k] || null;
      }
      if (Object.keys(diff).length === 0) { setEditing(false); return; }
      const { data } = await api.patch(`/contacts/${c.id}/details`, diff);
      onSaved?.(data || diff);
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed. Please retry.");
    } finally { setSaving(false); }
  }

  const editAction = editing ? (
    <div className="flex gap-1">
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-white rounded hover:bg-stone-800 disabled:opacity-50"
        data-testid={`pipeline-panel-summary-save-${c.id}`}
      ><Save className="w-2.5 h-2.5" /> Save</button>
      <button
        type="button"
        onClick={() => { setEditing(false); setErr(""); }}
        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-white text-stone-700 border border-stone-300 rounded hover:bg-stone-50"
        data-testid={`pipeline-panel-summary-cancel-${c.id}`}
      ><XIcon className="w-2.5 h-2.5" /> Cancel</button>
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-white text-stone-700 border border-stone-300 rounded hover:bg-stone-50"
      data-testid={`pipeline-panel-summary-edit-${c.id}`}
    ><Pencil className="w-2.5 h-2.5" /> Edit</button>
  );

  return (
    <PanelShell
      icon={UserIcon}
      title="Summary"
      testId="pipeline-panel-summary"
      action={editAction}
      bodyClass="flex flex-col"
    >
      {editing ? (
        <div className="space-y-2">
          <EditField label="First Name"      value={form.first_name}     onChange={(v) => setForm({ ...form, first_name: v })} testId={`pipeline-panel-summary-first-name-${c.id}`} />
          <EditField label="Last Name"       value={form.last_name}      onChange={(v) => setForm({ ...form, last_name: v })} testId={`pipeline-panel-summary-last-name-${c.id}`} />
          <EditField label="Email"           value={form.email}          onChange={(v) => setForm({ ...form, email: v })} />
          <EditField label="Phone"           value={form.telephone}      onChange={(v) => setForm({ ...form, telephone: v })} />
          <EditField label="Address"         value={form.address_line_1} onChange={(v) => setForm({ ...form, address_line_1: v })} />
          <EditField label="2nd Line of Address" value={form.address_line_2} onChange={(v) => setForm({ ...form, address_line_2: v })} />
          <EditField label="Town/City"       value={form.city}           onChange={(v) => setForm({ ...form, city: v })} />
          <EditField label="County/State"    value={form.county}         onChange={(v) => setForm({ ...form, county: v })} />
          <EditField label="Postcode"        value={form.postcode}       onChange={(v) => setForm({ ...form, postcode: v.toUpperCase() })} />
          <EditField label="Country"         value={form.country}        onChange={(v) => setForm({ ...form, country: v })} />
          {err && <div className="text-[11px] text-red-600">{err}</div>}
        </div>
      ) : (
        <>
          {/* Contact block */}
          <div className="space-y-3 flex-1">
            <SummaryRow icon={UserIcon} value={[c.first_name, c.last_name].filter(Boolean).join(" ")} />
            <SummaryRow icon={Mail}  value={c.email} />
            <SummaryRow icon={Phone} value={c.telephone || c.phone} />
            <div className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-stone-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                {c.address_line_1 && <div className="truncate">{c.address_line_1}</div>}
                {c.address_line_2 && <div className="truncate">{c.address_line_2}</div>}
                {c.city && <div className="truncate">{c.city}</div>}
                {c.county && <div className="truncate">{c.county}</div>}
                {c.postcode && (
                  <div className="mt-0.5">
                    <span>{c.postcode}</span>
                  </div>
                )}
                {c.country && <div className="truncate text-stone-500">{c.country}</div>}
                {!c.address_line_1 && !c.address_line_2 && !c.city && !c.county && !c.postcode && !c.country && (
                  <span className="text-stone-400 italic">no address on file</span>
                )}
              </div>
            </div>
          </div>
          {/* Date footer — sits at the bottom of the panel */}
          <div className="mt-4 pt-3 border-t border-stone-200/70 flex items-center gap-2 text-[11px] text-stone-600">
            <Calendar className="w-3.5 h-3.5 text-stone-400" />
            <span>
              {formatDate(c.date_added || c.date)}
              {typeof daysInStage === "number" && <span> · {daysInStage} days ago</span>}
            </span>
          </div>
        </>
      )}
    </PanelShell>
  );
}

function SummaryRow({ icon: Icon, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-stone-400 mt-0.5 shrink-0" />
      <span className="break-all">{value || <span className="text-stone-400">—</span>}</span>
    </div>
  );
}

function EditField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-bold text-stone-500">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-0.5 w-full text-xs border border-stone-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-stone-900"
      />
    </label>
  );
}

// ---------------------------------------------------------------------
// MOVE TO STAGE — 2 columns × 3 rows in the reference order.
const STAGE_GRID = [
  { key: "new",           label: "New" },
  { key: "contacted",     label: "Contacted" },
  { key: "qualified",     label: "Interested" },
  { key: "dormant",       label: "Dormant" },
  { key: "follow_up_due", label: "Follow-up Due" },
  { key: "lost",          label: "Lost" },
];

function StagePanel({ contact, onStageChange, onDemote, stretch = false }) {
  // In `stretch` mode the six pills spread out across a single row so
  // the panel can span the full width of the expanded card cleanly.
  const gridCls = stretch
    ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2"
    : "grid grid-cols-2 gap-2";
  // Mirror the admin check used elsewhere in the hub (Layout.js /
  // ProjectsThisMonthModal.jsx). The Mailchimp unsubscribe row is
  // admin-only and only renders when we have an email to send.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const email = (contact?.email || contact?.email_raw || "").trim();
  const [mcBusy, setMcBusy] = useState(false);
  const removeFromMailchimp = async () => {
    if (mcBusy) return;
    setMcBusy(true);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/mailchimp/unsubscribe`);
      if (data?.status === "unsubscribed") {
        toast.success(data.message || "Unsubscribed from Mailchimp.");
      } else if (data?.status === "already_unsubscribed") {
        toast(data.message || "Already unsubscribed in Mailchimp.");
      } else {
        toast(data?.message || "Mailchimp request completed.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : (detail?.message || err?.message || "Mailchimp request failed.");
      toast.error(message);
    } finally {
      setMcBusy(false);
    }
  };
  return (
    <PanelShell icon={Clock} title="Move to Stage" testId="pipeline-panel-stage">
      <div className={gridCls}>
        {STAGE_GRID.map((s) => {
          const isCurrent = contact.pipeline_status === s.key;
          const spec = STAGE[s.key];
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onStageChange?.(contact.id, s.key)}
              disabled={isCurrent}
              aria-current={isCurrent}
              className={`px-2 py-2 text-[10px] font-bold uppercase tracking-wider border rounded-full transition-colors ${
                isCurrent ? `${spec.activeCls} cursor-default` : `${spec.cls} hover:bg-stone-50`
              }`}
              data-testid={`pipeline-panel-stage-${s.key}-${contact.id}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onDemote?.(contact.id)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 rounded-lg"
          data-testid={`pipeline-panel-demote-${contact.id}`}
        >
          <ArrowDownCircle className="w-3 h-3" /> Remove from sales pipeline
        </button>
        {isAdmin && email && (
          <button
            type="button"
            onClick={removeFromMailchimp}
            disabled={mcBusy}
            title="Unsubscribe this contact's email from the configured Mailchimp audience"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 rounded-lg disabled:opacity-50"
            data-testid={`pipeline-panel-mailchimp-unsubscribe-${contact.id}`}
          >
            {mcBusy
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <MailX className="w-3 h-3" />}
            Remove from Mailchimp
          </button>
        )}
      </div>
    </PanelShell>
  );
}

// ---------------------------------------------------------------------
// PLAN THEIR TERRITORY — compact panel; empty-state → OPEN BUILDER,
// linked → LINKED-PLAN card + SEE LINKED PLAN.
function TerritoryPanel({ contact, compact = false }) {
  const lp = contact.linked_plan;
  const href = lp
    ? `/territory-builder?plan_id=${lp.id}`
    : `/territory-builder?contact_id=${contact.id}`;
  const actionCls = `shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded ${
    lp ? "bg-emerald-700 text-white hover:bg-emerald-800" : "bg-stone-950 text-white hover:bg-stone-800"
  }`;
  const actionLabel = lp ? "See linked plan" : "Open builder";

  // Compact = single-line horizontal strip (no PanelShell chrome) —
  // used inside the right-rail stack where vertical space is at a
  // premium. Matches the target visual "Joanna Foxley · 45 sectors
  // [SEE LINKED PLAN]". Falls back to the full panel when NOT compact.
  if (compact) {
    return (
      <div
        className="border-2 rounded-xl px-3 py-2 flex items-center gap-2 min-w-0"
        style={{ backgroundColor: "#fafbe9", borderColor: "#dddd16" }}
        data-testid="pipeline-panel-territory"
      >
        <Target className="w-3.5 h-3.5 text-stone-500 shrink-0" />
        <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-stone-700 shrink-0">Plan Their Territory</div>
        <div className="flex-1 min-w-0 text-xs text-stone-700 truncate">
          {lp ? (
            <>
              <span className="font-semibold text-stone-900">{lp.name || "Unnamed plan"}</span>
              {typeof lp.sectors_count === "number" && (
                <span className="text-stone-500"> · {lp.sectors_count} sectors</span>
              )}
              {typeof lp.total_homes === "number" && (
                <span className="text-stone-500"> · {lp.total_homes} homes</span>
              )}
            </>
          ) : (
            <span className="text-stone-500">No plan yet — build a mental territory plan for this contact.</span>
          )}
        </div>
        <a
          href={href}
          className={actionCls}
          data-testid={`pipeline-panel-territory-action-${contact.id}`}
        >
          <Target className="w-3 h-3" /> {actionLabel}
        </a>
      </div>
    );
  }

  return (
    <PanelShell
      icon={Target}
      title="Plan Their Territory"
      testId="pipeline-panel-territory"
      action={
        <a href={href} className={actionCls} data-testid={`pipeline-panel-territory-action-${contact.id}`}>
          <Target className="w-3 h-3" /> {actionLabel}
        </a>
      }
    >
      {lp ? (
        <div className="text-stone-700">
          <div className="font-semibold text-stone-900">{lp.name || "Unnamed plan"}</div>
          <div className="text-[11px] text-stone-500 mt-0.5">
            {typeof lp.total_homes === "number" && <>{lp.total_homes} homes</>}
            {typeof lp.sectors_count === "number" && <> · {lp.sectors_count} sectors</>}
          </div>
        </div>
      ) : (
        <p className="text-stone-600">Build a mental territory plan for this contact.</p>
      )}
    </PanelShell>
  );
}

// ---------------------------------------------------------------------
// CONVERT TO FRANCHISEE — light tint, Convert + Link to Existing.
// Sits in a half-width column beside CHANGE TYPE so the two actions
// share the visual weight of the reclassification workflow. Buttons
// stack vertically so short labels don't wrap in the narrow column.
function ConvertPanel({ contact, onConvert, onLinkExisting }) {
  const [converting, setConverting] = useState(false);
  const isLicenceEnq = contact.source === "licence_enquiry";
  const label = isLicenceEnq ? "Convert to Licencee" : "Convert to Franchisee";
  const already = !!contact.converted_to_franchisee_id;
  return (
    <PanelShell
      icon={Award}
      title={already ? "Already converted" : label}
      testId="pipeline-panel-convert"
      tone={already ? "emerald" : "tinted"}
    >
      <div className="flex flex-col gap-1.5">
        {already ? (
          <button
            type="button"
            onClick={() => onConvert?.(contact, true)}
            data-testid={`pipeline-panel-convert-view-${contact.id}`}
            className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-white text-emerald-800 border border-emerald-300 hover:bg-emerald-100 rounded-full"
          >View {contact.converted_to_record_type === "licencee" ? "Licencee" : "Franchisee"}</button>
        ) : (
          <>
            <button
              type="button"
              onClick={async () => {
                const name = displayNameFor(contact);
                if (!window.confirm(`${label} for ${name}?`)) return;
                setConverting(true);
                try { await onConvert?.(contact, false); }
                finally { setConverting(false); }
              }}
              disabled={converting}
              data-testid={`pipeline-panel-convert-btn-${contact.id}`}
              className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#c9c914] rounded-full disabled:opacity-50"
            ><Award className="w-3 h-3" /> {converting ? "…" : "Convert"}</button>
            {onLinkExisting && (
              <button
                type="button"
                onClick={() => onLinkExisting?.(contact)}
                data-testid={`pipeline-panel-convert-link-existing-${contact.id}`}
                className="inline-flex items-center justify-center gap-1 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-white text-stone-800 border border-stone-300 hover:bg-stone-50 rounded-full"
              ><Link2 className="w-3 h-3" /> Link to existing</button>
            )}
          </>
        )}
      </div>
    </PanelShell>
  );
}

// ---------------------------------------------------------------------
// CHANGE TYPE — reclassify the enquiry between Franchise / Licence /
// General without leaving the Sales Pipeline row. Backed by the same
// `/contacts/{id}/move` endpoint used by the drawer's "Change type"
// menu, so behaviour stays consistent across list + drawer views.
const CHANGE_TYPE_OPTIONS = [
  { key: "franchise", label: "Franchise", source: "franchise_enquiry" },
  { key: "licence",   label: "Licence",   source: "licence_enquiry" },
  { key: "general",   label: "General",   source: "general_enquiry" },
];

function ChangeTypePanel({ contact, onChangeSource }) {
  const currentSource = contact.source || "general_enquiry";
  const currentOpt = CHANGE_TYPE_OPTIONS.find((o) => o.source === currentSource);
  const [busy, setBusy] = useState(false);
  const canChange = !!onChangeSource;
  return (
    <PanelShell
      icon={ArrowRightLeft}
      title="Change Type"
      testId="pipeline-panel-change-type"
    >
      <div className="flex flex-col gap-1.5">
        <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500">
          Current: <span className="text-stone-800">{currentOpt?.label || "General"}</span>
        </div>
        <select
          value={currentOpt?.key || "general"}
          disabled={!canChange || busy}
          onChange={async (e) => {
            const target = e.target.value;
            if (target === currentOpt?.key) return;
            const label = CHANGE_TYPE_OPTIONS.find((o) => o.key === target)?.label || target;
            const name = displayNameFor(contact);
            if (!window.confirm(`Reclassify ${name} as a ${label} enquiry?`)) return;
            setBusy(true);
            try { await onChangeSource?.(contact, target); }
            finally { setBusy(false); }
          }}
          className="w-full text-xs border border-stone-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-stone-900 disabled:opacity-50"
          data-testid={`pipeline-panel-change-type-select-${contact.id}`}
        >
          {CHANGE_TYPE_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>{o.label} enquiry</option>
          ))}
        </select>
      </div>
    </PanelShell>
  );
}

// ---------------------------------------------------------------------
// FOLLOW-UP STATUS — spans central columns 2 + 3. Compact vertical
// height with the action button aligned right so the panel doesn't
// feel left-heavy.
function FollowUpPanel({ contact, onMarkFollowUpSent }) {
  const recorded = Number(contact.follow_up_sent_count || 0) >= 1;
  if (recorded) {
    return (
      <PanelShell icon={Clock} title="Follow-up Status" testId="pipeline-panel-followup" tone="emerald">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          <span className="text-emerald-900">Follow-up recorded on this contact.</span>
        </div>
      </PanelShell>
    );
  }
  return (
    <PanelShell icon={Clock} title="Follow-up Status" testId="pipeline-panel-followup" tone="amber">
      <div className="flex flex-col gap-2">
        <p className="text-amber-900">
          Already sent a follow-up outside the system? Mark it as done so
          this contact won&apos;t drop into <strong>Follow-up Due</strong>.
        </p>
        <button
          type="button"
          onClick={() => onMarkFollowUpSent?.(contact.id)}
          className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-stone-950 hover:bg-amber-600 rounded-full"
          data-testid={`pipeline-panel-mark-followup-${contact.id}`}
        >
          <CheckCircle2 className="w-3 h-3" /> Mark follow-up already sent
        </button>
      </div>
    </PanelShell>
  );
}

// ---------------------------------------------------------------------
// NOTES & RELATED INFORMATION — 2-column bundle that packs the Notes
// editor next to Sales Pack + Supplementary Notes so the wide-desktop
// half-width slot gets used properly (previously Notes + Sales Pack
// stacked vertically in a narrow column with lots of dead space).
//
// The three inner sections are inlined (not full PanelShells) so we
// only render ONE outer chrome — matches the reference visual.
function NotesAndRelatedPanel({ contact, onContactUpdated }) {
  const c = contact;
  const pack = c?.sales_pack_received || null;
  const [previewOpen, setPreviewOpen] = useState(false);
  return (
    <>
    <PanelShell
      icon={FileText}
      title="Notes & Related Information"
      testId={`pipeline-panel-notesbundle-${c.id}`}
      bodyClass="!p-0"
    >
      <div className="grid gap-0 grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-stone-200/70">
        {/* ── Left ─ Notes editor */}
        <div className="p-4 flex flex-col gap-2 min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-stone-500">
            <FileText className="w-3 h-3" /> Notes
          </div>
          <div className="flex-1 min-h-[140px] flex" data-testid={`pipeline-notes-inline-${c.id}`}>
            <AdminNotesEditor
              contact={c}
              onUpdated={(id, notes, ts) =>
                onContactUpdated?.(id, { admin_notes: notes, admin_notes_updated_at: ts })
              }
              fullHeight
            />
          </div>
        </div>

        {/* ── Right ─ Sales pack + supplementary notes */}
        <div className="p-4 flex flex-col gap-4 min-w-0">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-stone-500 mb-2">
              <Send className="w-3 h-3" /> Sales Pack Received
            </div>
            {pack ? (
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="w-full text-left rounded-md border border-stone-200 bg-white hover:border-stone-950 hover:bg-stone-50 px-3 py-2 transition-colors"
                data-testid={`sales-pack-open-preview-inline-${c.id}`}
                title="Preview the PDF that was sent"
              >
                <div className="text-sm font-semibold text-stone-950 break-words">
                  {pack.title || pack.file_name || pack.template_name || "Sales pack"}
                </div>
                {pack.sent_at && (
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    Sent {formatSentAt(pack.sent_at)}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mt-1">
                  Click to preview
                </div>
              </button>
            ) : (
              <div className="text-xs text-stone-500 italic" data-testid={`sales-pack-empty-inline-${c.id}`}>
                No sales pack sent yet.
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-stone-500 mb-2">
              <MessageSquare className="w-3 h-3" /> Supplementary Notes
            </div>
            {c?.supplementary_notes ? (
              <div className="text-xs text-stone-800 whitespace-pre-wrap break-words" data-testid={`supplementary-notes-${c.id}`}>
                {c.supplementary_notes}
              </div>
            ) : (
              <div className="text-xs text-stone-500 italic">
                Add any supplementary notes or context via the contact drawer.
              </div>
            )}
          </div>
        </div>
      </div>
    </PanelShell>
    {previewOpen && pack && (
      <SalesPackPreviewModal pack={pack} onClose={() => setPreviewOpen(false)} />
    )}
    </>
  );
}


// ---------------------------------------------------------------------
// NOTES — right column, TOP half. Textarea shrinks (was 220px min,
// now 120px) so the new "Sales Pack Received" panel gets meaningful
// space in the bottom half of the same column. Admins still get a
// comfortable jot area for call notes but don't waste column real
// estate when notes are short.
function NotesPanel({ contact, onChanged }) {
  return (
    <PanelShell
      icon={FileText}
      title="Notes"
      testId="pipeline-panel-notes"
      bodyClass="flex flex-col"
    >
      <div className="flex-1 min-h-[120px] flex">
        <AdminNotesEditor
          contact={contact}
          onUpdated={(id, notes, ts) =>
            onChanged?.(id, { admin_notes: notes, admin_notes_updated_at: ts })
          }
          fullHeight
        />
      </div>
    </PanelShell>
  );
}

// ---------------------------------------------------------------------
// SALES PACK RECEIVED — right column, BOTTOM half. Surfaces the most
// recent landing-page PDF this contact has been sent so admins can
// see at a glance which sales pack a lead has been given (Aug 26 no-
// flex vs June 26 inc-flex etc.) without having to open the
// correspondence timeline. Clicking the pill opens an inline pdfjs
// preview of the linked file.
//
// Data source: ``contact.sales_pack_received`` — populated by
// ``sales_pack_resolver`` at send time and by the one-off backfill
// endpoint. When null, we show a friendly empty state instead of
// hiding the panel (per user request — the panel is a visible
// anchor for the "did we send them anything?" question, even when
// the answer is no).
function SalesPackPanel({ contact }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const pack = contact?.sales_pack_received || null;
  return (
    <>
      <PanelShell
        icon={Send}
        title="Sales Pack Received"
        testId="pipeline-panel-sales-pack"
      >
        {pack ? (
          <div className="flex flex-col gap-2" data-testid="sales-pack-received">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="text-left rounded-md border border-stone-200 bg-white hover:border-stone-950 hover:bg-stone-50 px-3 py-2 transition-colors"
              data-testid="sales-pack-open-preview"
              title="Preview the PDF that was sent"
            >
              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
                Latest pack sent
              </div>
              <div className="text-sm font-semibold text-stone-950 mt-0.5 break-words">
                {pack.title || pack.file_name || pack.template_name || "Sales pack"}
              </div>
              {pack.file_name && pack.file_name !== pack.title && (
                <div className="text-xs text-stone-500 mt-0.5 truncate" title={pack.file_name}>
                  {pack.file_name}
                </div>
              )}
              {pack.sent_at && (
                <div className="text-[11px] text-stone-500 mt-1">
                  Sent {formatSentAt(pack.sent_at)}
                </div>
              )}
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mt-1">
                Click to preview
              </div>
            </button>
          </div>
        ) : (
          <div
            className="text-xs text-stone-500 italic px-1 py-2"
            data-testid="sales-pack-empty"
          >
            No sales pack sent yet
          </div>
        )}
      </PanelShell>
      {previewOpen && pack?.file_key && (
        <SalesPackPreviewModal
          pack={pack}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}

// One-off admin utility surfaced discreetly at the top-right of the
// tabs. Walks every pipeline contact and stamps ``sales_pack_received``
// from their latest send. Idempotent — safe to run again after Paul
// renames landing pages or issues new templates. The button reflects
// the last run's stats so admins get immediate confirmation.
function SalesPackBackfillButton() {
  const [state, setState] = useState({ status: "idle", stats: null, error: null });
  const run = async () => {
    if (state.status === "running") return;
    if (!window.confirm(
      "Backfill Sales Pack Received for every contact currently in the pipeline?\n\n"
      + "This walks each contact's email history and stamps the latest landing-page PDF "
      + "sent to them. Safe to re-run — it only refreshes with newer data.",
    )) return;
    setState({ status: "running", stats: null, error: null });
    try {
      const { data } = await api.post("/admin/sales-pack/backfill");
      setState({ status: "done", stats: data.stats, error: null });
      // Nudge a reload so the newly-stamped rows show their packs.
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      setState({
        status: "error",
        stats: null,
        error: e?.response?.data?.detail || "Backfill failed",
      });
    }
  };
  const label = state.status === "running"
    ? "Backfilling…"
    : state.status === "done"
      ? `Done — ${state.stats?.contacts_with_pack || 0} stamped`
      : state.status === "error"
        ? "Retry backfill"
        : "Backfill sales packs";
  return (
    <button
      type="button"
      onClick={run}
      disabled={state.status === "running"}
      title={state.error ? String(state.error) : "One-off migration: fill Sales Pack Received from email history"}
      className="mr-2 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold rounded border border-stone-300 text-stone-600 hover:text-stone-950 hover:border-stone-950 disabled:opacity-50"
      data-testid="sales-pack-backfill-btn"
    >
      {label}
    </button>
  );
}

function formatSentAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    // Match the compact 'DD/MM/YYYY' Paul expects across the pipeline
    // rather than the browser's locale-default which mixes formats.
    return d.toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return "";
  }
}

// Full-screen modal preview of the PDF that was sent. Uses the same
// react-pdf viewer path as the contract signing modal (via ``iframe``
// with the presigned R2 URL — simpler than mounting pdfjs here since
// we don't need scroll-to-end gating; a plain browser PDF viewer is
// enough for a quick reference glance).
function SalesPackPreviewModal({ pack, onClose }) {
  const [state, setState] = useState({ status: "loading", url: null, error: null });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/sales-pack/preview-url", {
          params: { file_key: pack.file_key },
        });
        if (cancelled) return;
        setState({ status: "ready", url: data.url, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({
          status: "error",
          url: null,
          error: e?.response?.data?.detail || "Could not load the sales pack.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [pack.file_key]);
  return (
    <div
      className="fixed inset-0 z-[100] bg-stone-950/70 flex items-stretch justify-center p-4"
      onClick={onClose}
      data-testid="sales-pack-preview-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b bg-stone-50">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
              Sales Pack Received
            </div>
            <div className="text-sm font-semibold truncate" title={pack.title || pack.file_name}>
              {pack.title || pack.file_name || "Sales pack"}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {state.url && (
              <a
                href={state.url}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-stone-700 border border-stone-300 rounded hover:bg-white"
                data-testid="sales-pack-open-new-tab"
              >
                Open in new tab
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-stone-100"
              data-testid="sales-pack-close-preview"
              aria-label="Close preview"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-stone-100">
          {state.status === "loading" && (
            <div className="h-full flex items-center justify-center text-stone-500 text-sm">
              Loading sales pack…
            </div>
          )}
          {state.status === "error" && (
            <div className="h-full flex items-center justify-center text-red-800 text-sm px-6 text-center">
              {typeof state.error === "string" ? state.error : "Could not load the sales pack."}
            </div>
          )}
          {state.status === "ready" && state.url && (
            <iframe
              src={state.url}
              title={pack.title || pack.file_name || "Sales pack preview"}
              className="w-full h-full min-h-[560px] border-0"
              data-testid="sales-pack-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Discovery Portal panel (V1)
//
// Composes three admin controls in one card:
//   1. Proposed Territory picker (pulls from the same territory plans
//      collection the admin builder writes to)
//   2. Shadow Day configurator (date + HQ/regional toggle + franchisee
//      searchable picker when regional)
//   3. Persistent private-link generator — one click both ensures the
//      contact has a token and copies the URL to the admin's clipboard
//      so they can paste it into email/WhatsApp/etc.
// ---------------------------------------------------------------------
function DiscoveryPortalPanel({ contact }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [copyState, setCopyState] = useState("");
  const [plans, setPlans] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState("");
  // Confidentiality Agreement status is a read-only mirror of the
  // signing state stored in ``discovery_confidentiality_agreements``.
  // We fetch it separately from the main portal payload so we can
  // refresh it after every Shadow Day change (activation depends on
  // ``shadow_day_booked`` + ``shadow_day_date``) without re-loading
  // the whole panel.
  const [confidentiality, setConfidentiality] = useState(null);

  // Shared loader — called from both the initial mount effect AND
  // after every ``patch`` so Shadow Day edits that flip the
  // agreement between not-activated / awaiting / signed refresh in
  // real time without a full remount.
  const loadConfidentiality = React.useCallback(async () => {
    try {
      const { data } = await api.get(
        `/admin/contacts/${contact.id}/discovery-portal/confidentiality`,
      );
      setConfidentiality(data || null);
    } catch {
      // Swallow — a missing agreement block simply hides the row.
      setConfidentiality(null);
    }
  }, [contact.id]);

  // Load the portal overview once on mount / when the contact swaps.
  React.useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    (async () => {
      try {
        const { data } = await api.get(`/admin/contacts/${contact.id}/discovery-portal`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e?.response?.data?.detail || "Failed to load" });
      }
    })();
    loadConfidentiality();
    return () => { cancelled = true; };
  }, [contact.id, loadConfidentiality]);

  // Load territory plans + active franchisees for the pickers.
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/territory-plans");
        setPlans(Array.isArray(data) ? data : (data?.plans || data?.items || []));
      } catch { /* leave empty */ }
    })();
    (async () => {
      try {
        const { data } = await api.get("/franchisees", { params: { limit: 500 } });
        const list = Array.isArray(data) ? data : (data?.franchisees || data?.items || []);
        // Match the "Franchises / Licences" Active tab filter exactly
        // — tagged "Franchisee" AND not tagged "Demo"/"HQ" (which
        // live in the Other tab). Keeps the picker in sync with what
        // Sam sees on the live franchises page.
        const hasTag = (f, t) => Array.isArray(f.tags) ? f.tags.includes(t) : f.tags === t;
        setFranchisees(
          list.filter((f) => hasTag(f, "Franchisee") && !hasTag(f, "Demo") && !hasTag(f, "HQ")),
        );
      } catch { /* leave empty */ }
    })();
  }, []);

  const patch = async (fields) => {
    setSaving(true);
    try {
      await api.patch(`/admin/contacts/${contact.id}/discovery-portal`, fields);
      // Re-fetch so linked territory / shadow_day resolve fresh.
      const { data: refreshed } = await api.get(`/admin/contacts/${contact.id}/discovery-portal`);
      setState({ loading: false, data: refreshed, error: null });
      setLastSaved(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
      // Shadow Day changes toggle agreement activation — reload so
      // the read-only status row appears / hides accordingly.
      loadConfidentiality();
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Save failed" }));
    } finally {
      setSaving(false);
    }
  };

  // Email draft for the lead contact — built after the link is ensured,
  // opened via mailto. Never marks the contact as invited.
  const [emailDraft, setEmailDraft] = useState(null);
  const [emailNote, setEmailNote] = useState("");
  const linkBusy = React.useRef(false);
  const currentContactId = React.useRef(contact.id);
  React.useEffect(() => {
    currentContactId.current = contact.id;
    setEmailDraft(null);
    setEmailNote("");
  }, [contact.id]);

  const markInvited = async (contactId) => {
    try {
      const { data } = await api.post(`/admin/contacts/${contactId}/discovery-portal/mark-invited`);
      if (currentContactId.current !== contactId) return;
      setState((s) => ({ ...s, data: { ...(s.data || {}), discovery_portal_invited_at: data.invited_at } }));
    } catch (e) {
      toast.error(`Email draft opened, but the invitation could not be recorded: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const getLink = async () => {
    if (linkBusy.current) return;
    linkBusy.current = true;
    const requestedFor = contact.id;
    setEmailDraft(null);
    setEmailNote("");
    setCopyState("working");
    let url;
    try {
      const { data } = await api.post(`/admin/contacts/${requestedFor}/discovery-portal/ensure-token`);
      url = data.url;
      if (currentContactId.current !== requestedFor) return;
      // Refresh overview so the URL is displayed live.
      setState((s) => ({ ...s, data: { ...(s.data || {}), token: data.token, url: data.url } }));
      try {
        await navigator.clipboard.writeText(data.url);
        setCopyState("copied");
      } catch {
        setCopyState("ready");
      }
      setTimeout(() => setCopyState(""), 2500);
    } catch (e) {
      setCopyState("failed");
      setTimeout(() => setCopyState(""), 2500);
      return;
    } finally {
      if (!url || currentContactId.current !== requestedFor) linkBusy.current = false;
    }
    try {
      let draft;
      try {
        draft = buildDiscoveryPortalEmail({ email: contact.email, firstName: contact.first_name, portalUrl: url });
      } catch (e) {
        setEmailNote(`Discovery Portal link is ready, but the email draft could not be prepared: ${e.message}`);
        return;
      }
      setEmailDraft(draft);
      if (draft.warning) {
        setEmailNote(draft.warning);
        toast.warning(draft.warning);
        return;
      }
      try {
        openDiscoveryPortalEmail(draft);
      } catch {
        setEmailNote("Your link is ready. Use Open email draft to open your email app.");
        return;
      }
      markInvited(requestedFor);
    } finally {
      linkBusy.current = false;
    }
  };

  // Preview as Prospect — opens the SAME persistent token URL used
  // by "Provide Discovery Portal Link" in a new tab. Reuses the
  // idempotent ensure-token endpoint so if the contact already has
  // a token issued, that same URL is reopened; if not, one is
  // minted (matching the exact flow the admin would trigger anyway
  // by pressing "Provide Discovery Portal Link"). No preview mode,
  // no separate token, no bypass — the admin sees exactly what the
  // prospect sees.
  const [previewState, setPreviewState] = useState("");
  const previewAsProspect = async () => {
    // Open the tab synchronously first so Safari / strict pop-up
    // blockers don't reject it after an await round-trip.
    const win = window.open("about:blank", "_blank");
    setPreviewState("working");
    try {
      let url = state.data?.url;
      if (!url) {
        const { data } = await api.post(`/admin/contacts/${contact.id}/discovery-portal/ensure-token`);
        url = data.url;
        setState((s) => ({ ...s, data: { ...(s.data || {}), token: data.token, url: data.url } }));
      }
      if (win) {
        win.location = url;
      } else {
        // Pop-up blocked — fall back to same-tab nav guard.
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setPreviewState("");
    } catch (e) {
      if (win) win.close();
      setPreviewState("failed");
      setTimeout(() => setPreviewState(""), 2500);
    }
  };

  if (state.loading) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-5 text-sm text-stone-500">Loading Discovery Portal…</div>
    );
  }

  const d = state.data || {};
  const location = (d.shadow_day_location || "").toLowerCase();

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid="discovery-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-stone-500" />
          <div className="font-bold uppercase tracking-wider text-xs text-stone-800">Discovery Portal</div>
        </div>
        <div className="text-[10px] text-stone-500 tabular-nums">
          {saving ? "Saving…" : lastSaved ? `Saved ${lastSaved}` : ""}
        </div>
      </div>

      {state.error && (
        <div className="mb-3 text-xs text-red-600" data-testid="discovery-panel-error">{String(state.error)}</div>
      )}

      {d.token && !d.portal_active && (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="discovery-panel-access-paused"
        >
          <span className="inline-block w-2 h-2 mt-1 rounded-full bg-amber-500 shrink-0" />
          <div>
            <div className="font-bold uppercase tracking-wider">Access paused</div>
            <div className="mt-0.5">
              This contact is at <span className="font-semibold">{d.pipeline_status || "no stage"}</span> — the Discovery Portal is only visible while they&apos;re at <span className="font-semibold">Interested</span>. Move them back to reactivate access on the same link.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Territory picker */}
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1.5">
            Proposed Territory <span className="text-stone-400 font-normal normal-case">(defaults to the plan linked in Sales Pipeline)</span>
          </label>
          <select
            value={d.proposed_territory_plan_id || d.effective_territory_plan_id || ""}
            onChange={(e) => {
              const next = e.target.value || "";
              // Empty option = "reset to auto-linked from Sales Pipeline"
              // — write null so the portal keeps following the linked
              // plan if it changes. Any other selection becomes an
              // explicit admin override.
              patch({ proposed_territory_plan_id: next || null });
            }}
            data-testid="discovery-territory-select"
            className="w-full text-sm border border-stone-300 rounded-lg px-3 py-2 bg-white"
          >
            <option value="">— Use linked plan from Sales Pipeline —</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name || p.centre_postcode || p.id.slice(0, 8)}</option>
            ))}
          </select>
          {d.territory ? (
            <div className="text-[11px] text-stone-500 mt-1.5">
              {d.territory_auto_linked ? (
                <span className="text-emerald-700 font-semibold">Auto-linked from Sales Pipeline · </span>
              ) : null}
              {d.territory.sector_codes?.length || 0} sector{(d.territory.sector_codes?.length || 0) === 1 ? "" : "s"}
              {d.territory.home_count ? ` · ${d.territory.home_count} homes` : ""}
            </div>
          ) : (
            <div className="text-[11px] text-stone-400 mt-1.5">Portal will show &ldquo;Awaiting Territory Generation&rdquo;</div>
          )}
        </div>

        {/* Shadow Day */}
        <div className="lg:col-span-2">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1.5">
            Shadow Day
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="date"
              value={d.shadow_day_date ? String(d.shadow_day_date).slice(0, 10) : ""}
              onChange={(e) => patch({ shadow_day_date: e.target.value || null })}
              data-testid="discovery-shadow-date"
              className="text-sm border border-stone-300 rounded-lg px-3 py-2"
            />
            <select
              value={location || ""}
              onChange={(e) => patch({ shadow_day_location: e.target.value || null })}
              data-testid="discovery-shadow-location"
              className="text-sm border border-stone-300 rounded-lg px-3 py-2 bg-white"
            >
              <option value="">Location…</option>
              <option value="hq">Creative Mojo HQ (Sandra)</option>
              <option value="regional">Regional Franchisee</option>
            </select>
            {location === "regional" ? (
              <select
                value={d.shadow_day_regional_franchisee_id || ""}
                onChange={(e) => patch({ shadow_day_regional_franchisee_id: e.target.value || null })}
                data-testid="discovery-shadow-franchisee"
                className="text-sm border border-stone-300 rounded-lg px-3 py-2 bg-white"
              >
                <option value="">Choose franchisee…</option>
                {franchisees.map((f) => {
                  const label = [f.first_name, f.last_name].filter(Boolean).join(" ").trim() || f.organisation || f.email || f.id;
                  return <option key={f.id} value={f.id}>{label}</option>;
                })}
              </select>
            ) : (
              <div className="text-[11px] text-stone-400 self-center px-2">
                {location === "hq" ? "Sandra will be shown as host" : "Choose HQ or Regional"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confidentiality Agreement — READ-ONLY status mirror.
          Only rendered once the Shadow Day is booked + dated
          (i.e. the agreement has been activated). Admin sees the
          state but cannot sign / edit / delete from here — the
          only writer is the public signing form gated by the
          prospect's Discovery Portal token. */}
      {confidentiality?.activated ? (
        <div
          className="mt-5 pt-4 border-t border-stone-100"
          data-testid="discovery-confidentiality-status-row"
        >
          {confidentiality.status === "signed" ? (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold uppercase tracking-wider text-stone-900 mb-1" data-testid="discovery-confidentiality-status-heading">
                  Confidentiality Agreement —{" "}
                  <span className="text-emerald-700">Signed</span>
                </div>
                <dl className="text-[12px] text-stone-700 grid grid-cols-1 sm:grid-cols-[7rem,1fr] gap-x-3 gap-y-0.5">
                  <dt className="font-semibold text-stone-600">Signed by:</dt>
                  <dd data-testid="discovery-confidentiality-signed-by">{confidentiality.full_name || "—"}</dd>
                  <dt className="font-semibold text-stone-600">Typed signature:</dt>
                  <dd className="italic" data-testid="discovery-confidentiality-typed-signature">{confidentiality.typed_signature || "—"}</dd>
                  <dt className="font-semibold text-stone-600">Date signed:</dt>
                  <dd data-testid="discovery-confidentiality-date-signed">{formatSubmittedAt(confidentiality.submitted_at)}</dd>
                </dl>
              </div>
            </div>
          ) : (
            <div
              className="flex items-center gap-2 text-[12px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
              data-testid="discovery-confidentiality-status-awaiting"
              role="status"
            >
              <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" aria-hidden="true" />
              <span className="uppercase tracking-wider text-[11px]">Confidentiality Agreement</span>
              <span className="text-amber-700 font-normal">—</span>
              <span>Awaiting signature</span>
            </div>
          )}
        </div>
      ) : null}

      {/* Flex documents toggle — mirrors the standalone
          DiscoveryPortalPanel so both surfaces edit the same
          ``show_flex_discovery`` field on the contact. */}
      <div className="mt-5 pt-4 border-t border-stone-100" data-testid="discovery-flex-toggle-row">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 rounded border-stone-400 text-stone-900 focus:ring-stone-900"
            checked={!!d.show_flex_discovery}
            onChange={(e) => patch({ show_flex: e.target.checked })}
            data-testid="discovery-flex-toggle"
            disabled={saving}
          />
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-stone-900">
              Show Flex Documents in Discovery Portal
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              When enabled, the Main Franchise Agreement section shows two extra sample
              PDFs — Flex Payment Structure and Flex Addendum Agreement — pulled from
              <span className="font-mono text-[10px] bg-stone-100 px-1 py-0.5 rounded ml-1">All Files &gt; Admin &gt; Flex</span>.
              Independent from the Launch Portal Flex toggle. Default OFF.
            </div>
          </div>
        </label>
      </div>

      {/* Link generator */}
      <div className="mt-5 pt-4 border-t border-stone-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-[11px] text-stone-500 min-w-0 flex-1">
          {d.url ? (
            <span className="break-all"><Users className="w-3 h-3 inline mr-1 text-stone-400" /> {d.url}</span>
          ) : (
            <span className="text-stone-400">No link generated yet. Click the button to create one.</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={previewAsProspect}
            data-testid="discovery-preview-as-prospect"
            title="Open this contact's Discovery Portal in a new tab using their existing persistent token"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-stone-950 border border-stone-300 text-xs font-bold uppercase tracking-wider hover:bg-stone-50 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {previewState === "working"
              ? "Opening…"
              : previewState === "failed"
              ? "Failed"
              : "Preview as Prospect"}
          </button>
          <button
            type="button"
            onClick={getLink}
            disabled={copyState === "working"}
            data-testid="discovery-ensure-link"
            title={
              state.data?.portal_generated_at
                ? "Portal already issued — click to copy the link again."
                : "Mint a persistent Discovery Portal link for this prospect and copy it to your clipboard."
            }
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-stone-950 text-[#dddd16] text-xs font-bold uppercase tracking-wider hover:bg-stone-800 transition-colors"
          >
            <Copy className="w-3.5 h-3.5" />
            {copyState === "copied"
              ? "Copied ✓"
              : copyState === "working"
              ? "Working…"
              : copyState === "ready"
              ? "Ready — copy manually"
              : copyState === "failed"
              ? "Failed"
              : state.data?.portal_generated_at
              ? `Portal Link Generated ${(() => {
                  try {
                    const d = new Date(state.data.portal_generated_at);
                    const dd = String(d.getDate()).padStart(2, "0");
                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                    const yy = String(d.getFullYear()).slice(-2);
                    return `${dd}/${mm}/${yy}`;
                  } catch {
                    return "";
                  }
                })()}`
              : state.data?.token
              ? "Portal Link Generated"
              : "Provide Discovery Portal Link"}
          </button>
          {emailDraft?.mailtoHref && (
            <a
              href={emailDraft.mailtoHref}
              onClick={() => markInvited(contact.id)}
              data-testid="discovery-open-email-draft"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#dddd16] text-stone-950 text-xs font-bold uppercase tracking-wider hover:bg-[#c9c912] transition-colors"
            >
              <Mail className="w-3.5 h-3.5" /> Open email draft
            </a>
          )}
        </div>
      </div>
      {emailNote && (
        <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="discovery-email-note">
          {emailNote}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------
// View-Correspondence full-screen modal — the real implementation lives
// in its own file. Imported at the top of this module.
